/**
 * v1 -> v2 skaile.yaml migration utility.
 *
 * Detects a v1 workspace config (no `version` field) and upgrades it to v2.
 * The upgrade is non-destructive: all existing v1 fields are preserved and
 * the only structural change is adding `version: 2`.
 *
 * @docLink packages/library/api-reference#migration
 */

import * as fs from "node:fs";
import * as path from "node:path";
import { type Document, parseDocument, parse as yamlParse } from "yaml";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Result returned by {@link migrateWorkspaceConfig}.
 *
 * @docLink packages/library/api-reference#migration
 */
export interface MigrationResult {
  /** Whether the file was actually modified (false in dry-run or already v2). */
  migrated: boolean;
  /** Detected version after migration (or current version if no migration). */
  version: 1 | 2;
  /** Human-readable warnings about v1 patterns that need manual attention. */
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------

/**
 * Check if a workspace config needs migration and optionally perform it.
 *
 * The upgrade preserves all existing YAML content (including comments) and
 * inserts `version: 2` at the top of the document.
 *
 * @param dir - Directory containing skaile.yaml.
 * @param opts - `dryRun` to check without writing; `filename` to override the default filename.
 * @returns Migration result with `migrated` flag and any v1-pattern warnings.
 * @docLink packages/library/api-reference#migration
 */
export function migrateWorkspaceConfig(
  dir: string,
  opts?: { dryRun?: boolean; filename?: string },
): MigrationResult {
  const filename = opts?.filename ?? "skaile.yaml";
  const filePath = path.join(dir, filename);

  if (!fs.existsSync(filePath)) {
    return { migrated: false, version: 1, warnings: ["No skaile.yaml found"] };
  }

  const content = fs.readFileSync(filePath, "utf-8");
  const raw = yamlParse(content) as Record<string, unknown>;

  if (!raw || typeof raw !== "object") {
    return { migrated: false, version: 1, warnings: ["Invalid YAML content"] };
  }

  // Already v2
  if (raw.version === 2) {
    return { migrated: false, version: 2, warnings: [] };
  }

  const warnings: string[] = [];

  // Detect v1 patterns that need manual migration
  if (raw.dependencies && Array.isArray(raw.dependencies)) {
    warnings.push(
      "v1 'dependencies' field found. In v2, use 'library.instances' to reference " +
        "configured asset instances. Manual migration recommended.",
    );
  }

  if (raw.ai_resources) {
    warnings.push(
      "v1 'ai_resources' field found. In v2, sources are managed via " +
        "'skaile source add'. The field still works but is deprecated.",
    );
  }

  if (!opts?.dryRun) {
    // Use parseDocument to preserve comments and formatting.
    // Insert version: 2 at the top so it's the first key.
    const doc = parseDocument(content);
    if (doc.contents && "items" in doc.contents) {
      const map = doc.contents as Document.Parsed["contents"] & { items: unknown[] };
      // Remove existing version key if present, then prepend
      const items = map.items as { key?: { value?: unknown } }[];
      const idx = items.findIndex((p) => p.key?.value === "version");
      if (idx >= 0) items.splice(idx, 1);
      doc.set("version", 2);
      const added = items.pop();
      if (added) items.unshift(added);
    } else {
      doc.set("version", 2);
    }
    fs.writeFileSync(filePath, doc.toString(), "utf-8");
  }

  return {
    migrated: !opts?.dryRun,
    version: opts?.dryRun ? 1 : 2,
    warnings,
  };
}
